import alertService from '@jetbrains/ring-ui/components/alert-service/alert-service'
import type {SerializedError} from '@reduxjs/toolkit'
import type {FetchBaseQueryError} from '@reduxjs/toolkit/query'
import React from 'react'

import {useIsSakuraSelected} from '../../../hooks/useIsSakuraSelected'
import {extractError} from '../../../utils/extractError'
import {resolveRelative} from '../../../utils/url'

import {restApi} from 'src/services/rest'

const DEFAULT_TIMEOUT = 3000
export const useDisplayErrorAlert = (
  error?: FetchBaseQueryError | SerializedError | string | null,
  timeout = DEFAULT_TIMEOUT,
) => {
  React.useEffect(() => {
    if (error != null) {
      alertService.error(
        typeof error === 'string' ? error : extractError(error)?.toString(),
        timeout,
      )
    }
  }, [error, timeout])
}

export const useAgentsParamsLink = (param: string) => {
  const classicUIAgentsParamsLink = '/agents.html?tab=agentsParametersReport'
  const sakuraUIAgentsParamsLink = '/agents/overview?tab=agentsParametersReport'
  const isSakuraSelected = useIsSakuraSelected()

  return resolveRelative(
    isSakuraSelected
      ? `${sakuraUIAgentsParamsLink}&iframeSrc=${encodeURIComponent(
          resolveRelative(`${classicUIAgentsParamsLink}&embedded=true#${param}`),
        )}`
      : `${classicUIAgentsParamsLink}#${param}`,
  )
}

const getDomainFromUrl = (url: string) => {
  if (url === '') {
    return url
  }
  try {
    return new URL(url).hostname
  } catch {
    return null
  }
}
export const useDomainInfo = (domain: 'server' | 'all') => {
  const serverInfoFields = domain === 'all' ? 'webUrl,artifactsUrl' : 'webUrl'
  const {serverDomain, artifactsDomain, isFetching, isLoading} =
    restApi.endpoints.getServerInfo.useQuery(
      {fields: serverInfoFields},
      {
        refetchOnFocus: true,
        selectFromResult: ({data, isFetching, isLoading}) => ({
          isLoading,
          isFetching,
          serverDomain: data != null ? getDomainFromUrl(data.webUrl!) : null,
          artifactsDomain: data != null ? getDomainFromUrl(data.artifactsUrl!) : null,
        }),
      },
    )

  return {serverDomain, artifactsDomain, isFetching, isLoading}
}

export const useServerUrlInfo = () => {
  const {hostname, origin} = restApi.endpoints.getServerInfo.useQuery(
    {fields: 'webUrl'},
    {
      refetchOnFocus: true,
      selectFromResult: ({data}) => {
        let serverUrl = null
        try {
          serverUrl = data?.webUrl != null ? new URL(data.webUrl) : null
        } catch {
          // ignore
        }

        return {
          hostname: serverUrl?.hostname ?? null,
          origin: serverUrl?.origin ?? null,
        }
      },
    },
  )

  return {hostname, origin}
}
